import { Component, Input, EventEmitter, Output, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CameraDto, CameraConfigDto } from '../../../../../api/gen';

@Component({
  selector: 'app-zone-camera',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './zone-camera.component.html',
  styleUrl: './zone-camera.component.scss'
})
export class ZoneCameraComponent {
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);

  @Input() camera!: CameraDto;
  @Input() parkingSpaceId: string | null = null;
  @Input() isProcessing = false;
  @Input() isConnecting = false;
  @Input() isStartingLPR = false;
  @Output() removeCamera = new EventEmitter<string>();
  @Output() editCamera = new EventEmitter<CameraDto>();
  @Output() connectCamera = new EventEmitter<string>();
  @Output() startCameraLPRListener = new EventEmitter<string>();

  showAdvancedDetails = signal(false);

  get cameraConfig(): CameraConfigDto | undefined {
    return this.camera?.config;
  }

  /**
   * Server-computed (Camera.IsOnline, 75-minute window — cameras report roughly every 10 minutes
   * at best). Deliberately NOT recomputed here from lastHeartbeatAt with a short local window: a
   * window narrower than the real reporting interval reads every healthy camera as Offline between
   * beats (the exact bug this replaced — a 3-minute local heuristic flipped the badge to Offline a
   * few minutes after page load even though the camera was fine).
   * Falls back to deviceStatus only for cameras that have never reported a heartbeat at all.
   */
  get isOnline(): boolean {
    // TODO: drop the `as any` cast once the operator-app API client is regenerated
    // (npm run generate:api:operator) against a backend build that includes CameraDto.isOnline.
    const cam = this.camera as any;
    if (cam?.isOnline !== undefined) {
      return cam.isOnline === true;
    }
    return this.camera?.deviceStatus?.toLowerCase() === 'online';
  }

  get lastHeartbeatAt(): string | null {
    // TODO: drop the `as any` cast once the operator-app API client is regenerated
    // (npm run generate:api:operator) against a backend build that includes
    // CameraDto.lastHeartbeatDate.
    // No fallback to updatedAt/deviceStatus here on purpose — that showed a stale edit
    // timestamp as if it were a real heartbeat. Null means "no heartbeat recorded yet".
    const cam = this.camera as any;
    return cam?.lastHeartbeatDate ?? null;
  }

  get heartbeatRecencyText(): string {
    const ts = this.lastHeartbeatAt;
    if (!ts) return 'No heartbeat';
    const diffMs = Date.now() - new Date(ts).getTime();
    if (diffMs < 0) return 'Just now';
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return new Date(ts).toLocaleDateString();
  }

  get ipWithPort(): string | null {
    if (!this.cameraConfig?.ipAddress) return null;
    const port = this.cameraConfig.port ? `:${this.cameraConfig.port}` : '';
    return `${this.cameraConfig.ipAddress}${port}`;
  }

  copy(text: string | undefined, label: string): void {
    if (!text) return;
    navigator.clipboard.writeText(text);
    this.snackBar.open(`${label} copied to clipboard`, 'Close', { duration: 2500 });
  }

  toggleAdvanced(): void {
    this.showAdvancedDetails.update(v => !v);
  }

  onRemove(): void {
    if (this.camera?.id) {
      this.removeCamera.emit(this.camera.id);
    }
  }

  onConnect(): void {
    if (this.camera?.id) {
      this.connectCamera.emit(this.camera.id);
    }
  }

  onEdit(): void {
    if (this.camera) {
      this.editCamera.emit(this.camera);
    }
  }

  onStartLPRListener(): void {
    if (this.camera?.id) {
      this.startCameraLPRListener.emit(this.camera.id);
    }
  }

  onViewDetections(): void {
    if (this.camera?.id) {
      this.router.navigate(['/camera', this.camera.id, 'detections']);
    }
  }

  onViewEvents(): void {
    if (this.camera?.id) {
      this.router.navigate(['/camera', this.camera.id, 'events'], {
        queryParams: { parkingSpaceId: this.parkingSpaceId }
      });
    }
  }

  ConfidenceReferenceShowValue(confidenceReference: string): string {
    switch (confidenceReference) {
      case 'Scale0To100':
        return '0-100';
      case 'Scale0To1':
        return '0-1';
      case 'Scale0To255':
        return '0-255';
      default:
        return confidenceReference;
    }
  }
}
